import { PassengerSelection } from "@apac/checkout";
import { TravelerEditMode, TravellerForm } from "@apac/travelers";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useCheckoutStateSelector } from "@checkout/context";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useTravellerFormOverrides } from "@hsbc/portal";
import { Dialog, DialogContent, Icon, LazyLoading } from "@tenant/ui";
import {
  getMissingAirTravelerFields,
  useAirTravelerFormRequiredFields,
} from "../../hooks/useAirTravelerFormRequiredFields";
import {
  getFlightSearchDepartureDate,
  getIsDomesticFlight,
} from "../../selectors/common";
import { ConnectedPassengerError } from "../PassengerError/component";
import { useConnectedPassengerSelection } from "./hooks/useConnectedPassengerSelection";
import { InfantSeatPickerDialog } from "./InfantSeatPickerDialog";
import styles from "./styles.module.scss";

export const ConnectedPassengerSelection = () => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const [block, cn] = useModuleBEM(styles, "ConnectedPassengerSelection");

  // Get departure date from checkout context
  const departureDate = useCheckoutStateSelector(getFlightSearchDepartureDate);
  const isDomesticFlight = useCheckoutStateSelector(getIsDomesticFlight);

  // Per-airline overrides from the air-traveler-form-exception flag.
  // `require*` is what the flag actually says; in TravellerForm "require
  // implies show" so the matching `show*` props don't need to be set here.
  const requiredFields = useAirTravelerFormRequiredFields();

  const {
    context: {
      isLoading,
      disabled,
      passengerList,
      allSelectedPassengerIds,
      searchPassengerCounts,
      shouldOpenInfantSeatPicker,
      openPassengerFormModal,
      currentPassenger,
      passengerSearchCountsString,
    },
    handlers: {
      handleSelectPassenger,
      handleEditClick,
      handleDeletePassenger,
      handleClickAddTraveler,
      handleCloseForm,
      handleUpdatePassenger,
      handleInfantPickerModalClose,
      handleInfantPickerModalContinue,
    },
  } = useConnectedPassengerSelection();

  const missingRequiredFields = getMissingAirTravelerFields(
    currentPassenger,
    requiredFields
  );

  const { travellerFormOverrides } = useTravellerFormOverrides({
    travelerEditMode: TravelerEditMode.FLIGHTS_ONLY,
    showPassportDetails: !isDomesticFlight,
    requireGender: requiredFields.gender,
    requireNationality: requiredFields.nationality,
    requirePassportDetails: requiredFields.passport,
  });

  return (
    <div className={block}>
      <ConnectedPassengerError />

      {isLoading ? (
        <div className={cn("component-loading")}>
          <LazyLoading />
        </div>
      ) : (
        <>
          {!disabled ? (
            <button
              disabled={disabled}
              className={cn("button-add-passenger")}
              onClick={() => handleClickAddTraveler()}
            >
              <Icon iconName={IconNameEnum.plus} />
              {t("checkoutSection.passenger.button")}
            </button>
          ) : null}

          <div className={cn("passenger-search-counts")}>
            {passengerSearchCountsString}
          </div>

          <PassengerSelection
            passengerList={passengerList}
            handleSelectPassenger={handleSelectPassenger}
            handleEdit={handleEditClick}
            handleDelete={handleDeletePassenger}
            allSelectedPassengerIds={allSelectedPassengerIds}
            disabled={disabled}
            searchPassengerCounts={searchPassengerCounts}
            tripDate={departureDate ?? undefined}
          />

          <Dialog open={openPassengerFormModal} onClose={handleCloseForm}>
            <DialogContent fullScreen={!isDesktopAndUp} showOverlay={true}>
              <TravellerForm
                onSubmit={handleUpdatePassenger}
                onDelete={handleDeletePassenger}
                onClose={handleCloseForm}
                travelerEditMode={TravelerEditMode.FLIGHTS_ONLY}
                traveler={currentPassenger}
                dateFormat="DD/MM/YYYY"
                showPassportDetails={!isDomesticFlight}
                requireGender={requiredFields.gender}
                requireNationality={requiredFields.nationality}
                requirePassportDetails={requiredFields.passport}
                fieldOverrides={travellerFormOverrides}
                prefillNationality={false}
                enforceMinNameLength
                missingTravelerInfoError={
                  missingRequiredFields.gender ||
                  missingRequiredFields.nationality
                }
                missingPassportInfoError={missingRequiredFields.passport}
              />
            </DialogContent>
          </Dialog>

          <InfantSeatPickerDialog
            open={shouldOpenInfantSeatPicker}
            onClose={handleInfantPickerModalClose}
            onContinue={handleInfantPickerModalContinue}
          />
        </>
      )}
    </div>
  );
};
